import _ from "lodash";

import type { ProtocolTimeSeriesPreset } from "../../../../apis/types/analysisTypes";
import type {
	ProtocolCurveModelOption,
	ProtocolDashboardOptions,
	ProtocolDataNormalization,
	ProtocolGroundDataAssessment,
	ProtocolSummaryTablePreset,
	ProtocolTreatment,
	ProtocolTrialFilter,
} from "../../../../apis/types/protocolTypes";
import type { TimecourseData, TimecourseDataViewModel } from "../../../../apis/types/statisticsTypes";
import type {
	AssessmentKind,
	CurveModelAssessmentSelection,
	GroundDataAssessmentSelection,
	ProtocolAssessmentSelection,
	StandCountAssessmentSelection,
} from "./ProtocolAnalysisContext";

/** Semantic UI dropdown option. */
export type DropdownOption<TValue = string> = {
	key: string;
	value: TValue;
	text: string;
};

/**
 * One row of the "Assessment" dropdown shared by the box whisker, piano and summary table pages.
 * `kind` discriminates ground data, curve model and stand count options.
 */
export type AssessmentOption = DropdownOption & {
	kind: AssessmentKind;
	analysisId: string | null;
	analysisTypeId: string | null;
	curveModelAnalysisId: string | null;
	groundDataAssessmentId: string | null;
	unitName: string | null;
	/** Curve model timing name — only set for curve models. */
	plannedTimingName: string | null;
	order: number | null;
};

/** One row of the "Growth Phase" / "Planned Timing" dropdown. */
export type PlannedTimingOption = DropdownOption & {
	/** Ground data assessments that have data at this growth phase. */
	groundDataAssessmentIds: string[];
};

export type DataNormalizationOption = DropdownOption & {
	treatmentTypeId: string | null;
	disabled: boolean;
};

export type TrialFilterOption = DropdownOption & {
	filters: {
		quality: string[];
		status: string[];
		response: string[];
	};
};

/** The subset of trial metadata a saved trial filter is matched against. */
type FilterableTrialInfo = {
	trialId: string;
	trialDataQualityId: string | null;
	trialExclusionStatusId: string | null;
	trialSiteResponseId: string | null;
};

/** A plot analysis result row, narrowed to the fields the timecourse dropdowns are derived from. */
export type AnalysisResultForOptions = {
	trialId: string;
	analysisId: string | null;
	analysisTypeId: string | null;
	analysisTypeName: string | null;
	analysisName: string | null;
	orthoImageTypeId: string | null;
	orthoImageTypeName: string | null;
	quantifiedRegionTypeId: string | null;
	quantifiedRegionTypeName: string | null;
};

/**
 * The three timecourse dropdowns that together identify an analysis: analysis type + ortho +
 * quantified region.
 */
export type TimecourseSelection = {
	analysisTypeId: string | null;
	orthoImageTypeId: string | null;
	quantifiedRegionTypeId: string | null;
};

/** A time series preset, narrowed to what picking one has to resolve. */
export type TimecoursePresetOption = DropdownOption & {
	analysisId: string;
	analysisTypeId: string;
	quantifiedRegionTypeId: string;
	orthoImageTypeId: string;
};

export const CUSTOM_TRIAL_FILTER = "custom";
export const NO_SUMMARY_TABLE_PRESET = "none";

const STAND_COUNT_NAME = "Stand Count (LAI)";
const DEFAULT_TIMECOURSE_ANALYSIS_NAME = "Aerial NDVI";
const DEFAULT_QUANTIFIED_REGION_NAME = "plot-centered with subsamples";

/* -------------------------------------------------------------------------- */
/*  Dropdown option builders                                                  */
/* -------------------------------------------------------------------------- */

/**
 * Builds the "Assessment" dropdown: ground data assessments, then curve models, with Stand Count
 * pushed to the front when the protocol's trials have stand count data.
 *
 * `standCountAnalyses` is the `data` array from the client-wide analyses endpoint
 * (the GetAnalyses query in queries.ts) — stand count is not part of ProtocolDashboardOptions.
 */
export const getAssessmentOptions = (
	dashboardOptions: ProtocolDashboardOptions,
	standCountAnalyses?: { id: string; name: string; analysisTypeId: string }[],
): AssessmentOption[] => {
	const timingNames = _.keyBy(dashboardOptions.curveModelTimings, "id");

	const groundData: AssessmentOption[] = _.chain(dashboardOptions.groundDataAssessments)
		.sortBy("groundDataAssessmentName")
		.uniqBy("groundDataAssessmentId")
		.map((gda) => ({
			key: gda.groundDataAssessmentId,
			value: gda.groundDataAssessmentId,
			text: gda.groundDataAssessmentName,
			kind: "grounddata" as const,
			analysisId: null,
			analysisTypeId: null,
			curveModelAnalysisId: null,
			groundDataAssessmentId: gda.groundDataAssessmentId,
			unitName: gda.unitName,
			plannedTimingName: null,
			order: null,
		}))
		.value();

	// Ascending. `order` is null on most curve models, so unordered ones sort last
	// via Infinity.
	const curveModels: AssessmentOption[] = _.chain(dashboardOptions.curveModels)
		.sortBy([(cm: ProtocolCurveModelOption) => cm?.order || Infinity, "curveModelAnalysisAbbreviation"])
		.map((cm) => ({
			key: cm.curveModelName,
			value: cm.curveModelName,
			text: cm.displayName ?? cm.curveModelName,
			kind: "curvemodel" as const,
			analysisId: cm.analysisId,
			analysisTypeId: cm.analysisTypeId,
			curveModelAnalysisId: cm.curveModelAnalysisId,
			groundDataAssessmentId: null,
			unitName: cm.unitName,
			plannedTimingName: cm.curveModelTimingId ? (timingNames[cm.curveModelTimingId]?.name ?? null) : null,
			order: cm.order,
		}))
		.value();

	const options = [...groundData, ...curveModels];

	if (!dashboardOptions.trialsContainStandCounts || !standCountAnalyses?.length) {
		return options;
	}

	const standCount = _.find(standCountAnalyses, (a) => a.name.includes(STAND_COUNT_NAME));
	if (!standCount) return options;

	// Stand count goes to the top of the list
	return [
		{
			key: standCount.id,
			value: standCount.id,
			text: standCount.name,
			kind: "standcount" as const,
			analysisId: standCount.id,
			analysisTypeId: standCount.analysisTypeId,
			curveModelAnalysisId: null,
			groundDataAssessmentId: null,
			unitName: null,
			plannedTimingName: null,
			order: null,
		},
		...options,
	];
};

/** Builds the "Growth Phase" dropdown, one entry per distinct ground data growth phase. */
export const getPlannedTimingOptions = (
	groundDataAssessments: ProtocolGroundDataAssessment[],
): PlannedTimingOption[] => {
	return _.chain(groundDataAssessments)
		.uniqBy("groundDataGrowthPhaseId")
		.map((gda) => ({
			key: gda.groundDataGrowthPhaseId,
			value: gda.groundDataGrowthPhaseId,
			text: gda.groundDataGrowthPhaseName,
			groundDataAssessmentIds: _.chain(groundDataAssessments)
				.filter({ groundDataGrowthPhaseId: gda.groundDataGrowthPhaseId })
				.map("groundDataAssessmentId")
				.value(),
		}))
		.value();
};

/** Growth phases that actually have data for the selected ground data assessment. */
export const getPlannedTimingOptionsForAssessment = (
	plannedTimingOptions: PlannedTimingOption[],
	assessmentValue: string | null,
): PlannedTimingOption[] => {
	if (!assessmentValue) return [];
	return _.filter(plannedTimingOptions, (pto) => pto.groundDataAssessmentIds.includes(assessmentValue));
};

/**
 * Builds the "Data Normalization" dropdown.
 *
 * Two independent rules narrow this list:
 *  - `disabled`: a normalization is disabled unless the protocol has a treatment of that type
 *    (plus "none" and "% mean", which always apply). Keeps "N/A" out of the summary table.
 *  - `excludeNonTreatmentTypes`: the piano charts drop every option without a treatment type,
 *    except "% MEAN", because a piano chart is always relative to something.
 */
export const getDataNormalizationOptions = (
	dataNormalizations: ProtocolDataNormalization[],
	treatments?: ProtocolTreatment[],
	{ excludeNonTreatmentTypes = false }: { excludeNonTreatmentTypes?: boolean } = {},
): DataNormalizationOption[] => {
	const allowed = new Set(["none", "% mean"]);
	for (const t of treatments ?? []) {
		if (t.treatmentTypeName) allowed.add(`% ${t.treatmentTypeName.toLowerCase()}`);
	}

	const treatmentTypeIds = new Set(_.compact(_.map(treatments, "treatmentTypeId")));

	return _.chain(dataNormalizations)
		.filter((dn) => {
			if (excludeNonTreatmentTypes && !dn.treatmentTypeId && dn.name.toUpperCase() !== "% MEAN") {
				return false;
			}
			// options with no treatment type (none / % mean / rank order) are always available
			return !treatments || !dn.treatmentTypeId || treatmentTypeIds.has(dn.treatmentTypeId);
		})
		.map((dn) => ({
			key: dn.id,
			value: dn.id,
			text: dn.name,
			treatmentTypeId: dn.treatmentTypeId,
			disabled: !!treatments && !allowed.has(dn.name.toLowerCase()),
		}))
		.value();
};

export const getMeanComparisonOptions = (meanComparisons: { id: string; name: string }[]): DropdownOption[] => {
	return _.map(meanComparisons, ({ id, name }) => ({ key: id, value: id, text: name }));
};

/**
 * Alpha values arrive as strings ("0.05", "0.10", "0.20"). Sorted numerically so the order does not
 * depend on the zero-padding.
 */
export const getAlphaOptions = (alphas: { id: string; value: string }[]): DropdownOption[] => {
	return _.chain(alphas)
		.orderBy([(a) => Number(a.value)])
		.map(({ id, value }) => ({ key: id, value: id, text: value }))
		.value();
};

/** Summary table preset dropdown, with a leading "None" entry for hand-picked selections. */
export const getSummaryTablePresetOptions = (presets: ProtocolSummaryTablePreset[]): DropdownOption[] => {
	return [
		{ key: NO_SUMMARY_TABLE_PRESET, value: NO_SUMMARY_TABLE_PRESET, text: "None" },
		..._.chain(presets)
			.sortBy((p) => p.name)
			.map((p) => ({ key: p.id, value: p.id, text: p.name }))
			.value(),
	];
};

/* -------------------------------------------------------------------------- */
/*  Assessment selection                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Resolves an "Assessment" dropdown choice into the id set the analysis endpoints expect.
 *
 * Fields that do not apply to the chosen kind are null rather than "" — pass `?? ""` at the call
 * site if the endpoint needs an empty string.
 */
export const resolveAssessmentSelection = (option: AssessmentOption | undefined) => {
	if (!option) {
		return {
			assessmentValue: null,
			assessmentKind: null,
			analysisId: null,
			analysisTypeId: null,
			curveModelAnalysisId: null,
			groundDataAssessmentId: null,
		};
	}

	return {
		assessmentValue: option.value,
		assessmentKind: option.kind,
		analysisId: option.analysisId,
		analysisTypeId: option.analysisTypeId,
		curveModelAnalysisId: option.curveModelAnalysisId,
		groundDataAssessmentId: option.groundDataAssessmentId,
	};
};

/** Only ground data assessments are collected at a growth phase. */
export const assessmentUsesPlannedTiming = (kind: AssessmentKind | null) => kind === "grounddata";

/* -------------------------------------------------------------------------- */
/*  Timecourse dropdowns (derived from fetched results, not dashboardOptions)  */
/* -------------------------------------------------------------------------- */

/**
 * Every analysis the timecourse can show, labeled by analysis name (ex: "Aerial NDVI"). The value is
 * an analysisId. Not a dropdown: the user picks an analysis type, ortho and quantified region, and
 * the analysis follows (see resolveTimecourseSelection). Used to validate a selection and to pick
 * the default.
 */
export const getTimecourseAnalysisOptions = (results: AnalysisResultForOptions[]): DropdownOption[] => {
	return _.chain(results)
		.filter((r) => !!r.analysisTypeId && !!r.analysisId && !!r.analysisName)
		.uniqBy("analysisId")
		.map((r) => ({ key: r.analysisId!, value: r.analysisId!, text: r.analysisName! }))
		.value();
};

/**
 * The timecourse's "Analysis Type" dropdown. The value is an analysisTypeId, not an ortho type.
 */
export const getTimecourseAnalysisTypeOptions = (results: AnalysisResultForOptions[]): DropdownOption[] => {
	return _.chain(results)
		.filter((r) => !!r.analysisTypeId && !!r.analysisTypeName)
		.uniqBy("analysisTypeId")
		.map((r) => ({ key: r.analysisTypeId!, value: r.analysisTypeId!, text: r.analysisTypeName! }))
		.value();
};

/**
 * The timecourse's "Ortho" dropdown. The value is an orthoImageTypeId and the label its display
 * name. Several analyses can share an ortho, so this is not the analysis itself; see
 * resolveTimecourseSelection. Narrowed to one analysis type when given.
 */
export const getTimecourseOrthoOptions = (
	results: AnalysisResultForOptions[],
	analysisTypeId?: string | null,
): DropdownOption[] => {
	return _.chain(results)
		.filter((r) => !!r.orthoImageTypeId && !!r.orthoImageTypeName)
		.filter((r) => !analysisTypeId || r.analysisTypeId === analysisTypeId)
		.uniqBy("orthoImageTypeId")
		.map((r) => ({ key: r.orthoImageTypeId!, value: r.orthoImageTypeId!, text: r.orthoImageTypeName! }))
		.value();
};

/**
 * Resolves an analysis type + ortho + quantified region choice into the analysis it identifies. That
 * combination is unique, so there is no id to look up, unlike a preset.
 *
 * The analysis type is authoritative. Ortho and then quantified region are each kept if they still
 * exist beneath the choice above them, and otherwise fall back to that level's default, so changing
 * a dropdown never leaves a combination with no data. Returns undefined when the analysis type has
 * no analyses.
 */
export const resolveTimecourseSelection = (
	results: AnalysisResultForOptions[],
	desired: TimecourseSelection,
): (TimecourseSelection & { analysisId: string; analysisName: string | null }) | undefined => {
	const typeRows = _.filter(results, (r) => !!r.analysisId && r.analysisTypeId === desired.analysisTypeId);

	const orthoImageTypeId = _.some(typeRows, (r) => r.orthoImageTypeId === desired.orthoImageTypeId)
		? desired.orthoImageTypeId
		: (typeRows[0]?.orthoImageTypeId ?? null);
	const orthoRows = _.filter(typeRows, (r) => r.orthoImageTypeId === orthoImageTypeId);

	const quantifiedRegionTypeId = _.some(orthoRows, (r) => r.quantifiedRegionTypeId === desired.quantifiedRegionTypeId)
		? desired.quantifiedRegionTypeId
		: (getDefaultQuantifiedRegionId(orthoRows) ?? null);

	const match = _.find(orthoRows, (r) => r.quantifiedRegionTypeId === quantifiedRegionTypeId);
	if (!match) return undefined;

	return {
		analysisId: match.analysisId!,
		analysisName: match.analysisName,
		analysisTypeId: desired.analysisTypeId,
		orthoImageTypeId,
		quantifiedRegionTypeId,
	};
};

/**
 * The preset matching the current selection, if any, so the Assessment dropdown can follow edits
 * made in the Analysis Type, Ortho and Quantified Region dropdowns. A preset the user picked
 * (`preferredPresetId`) wins over another preset covering the same analysis.
 */
export const findMatchingTimecoursePreset = (
	presets: TimecoursePresetOption[] | undefined,
	selection: { analysisId: string | null; analysisTypeId: string | null; quantifiedRegionTypeId: string | null },
	preferredPresetId?: string | null,
): TimecoursePresetOption | undefined => {
	const matches = _.filter(
		presets,
		(p) =>
			p.analysisId === selection.analysisId &&
			p.analysisTypeId === selection.analysisTypeId &&
			p.quantifiedRegionTypeId === selection.quantifiedRegionTypeId,
	);

	return _.find(matches, (p) => p.value === preferredPresetId) ?? matches[0];
};

/**
 * Defaults to Aerial NDVI, but only if at least two trials have it — a single-trial analysis
 * cannot be compared across sites. Otherwise falls back to the first analysis with two or more
 * trials, then to whatever is first in the list.
 */
export const getDefaultTimecourseAnalysisId = (results: AnalysisResultForOptions[]): string | undefined => {
	const options = getTimecourseAnalysisOptions(results);
	const trialsPerAnalysis = _.chain(results)
		.filter((r) => !!r.analysisId && !!r.analysisName)
		.groupBy("analysisId")
		.mapValues((rows) => _.uniqBy(rows, "trialId").length)
		.value();

	const ndviId = _.find(options, { text: DEFAULT_TIMECOURSE_ANALYSIS_NAME })?.value;
	if (ndviId && (trialsPerAnalysis[ndviId] ?? 0) >= 2) return ndviId;

	const firstComparable = _.find(options, (o) => (trialsPerAnalysis[o.value] ?? 0) > 1)?.value;

	return firstComparable ?? options[0]?.value;
};

/**
 * The timecourse's "Assessment" dropdown.
 *
 * A time series preset is the real unit of selection here: picking one resolves analysisId,
 * analysisTypeId and quantifiedRegionTypeId together.
 *
 * Presets are restricted to combinations the fetched results actually contain — a preset with no
 * data would resolve to an empty chart.
 */
export const getTimecoursePresetOptions = (
	timeSeriesPresets: ProtocolTimeSeriesPreset[],
	results: AnalysisResultForOptions[],
): TimecoursePresetOption[] => {
	return _.chain(timeSeriesPresets)
		.filter((tsp) =>
			_.some(
				results,
				(r) =>
					r.analysisId === tsp.analysisId &&
					r.analysisTypeId === tsp.analysisTypeId &&
					(r.quantifiedRegionTypeId === tsp.quantifiedRegionTypeId ||
						r.quantifiedRegionTypeId === tsp.secondaryQuantifiedRegionTypeId),
			),
		)
		.orderBy(["order", "name"])
		.map((tsp) => ({
			key: tsp.id,
			value: tsp.id,
			// a nameless preset gets an empty label
			text: tsp.name ?? "",
			analysisId: tsp.analysisId,
			analysisTypeId: tsp.analysisTypeId,
			quantifiedRegionTypeId: tsp.quantifiedRegionTypeId,
			orthoImageTypeId: tsp.orthoImageTypeId,
		}))
		.value();
};

/**
 * Resolves a timecourse preset choice into the id set the protocol endpoints expect, including the
 * uniformity flag — which is only derivable once analysisTypeId is known, and is why the Analysis
 * dropdown alone cannot drive this selection.
 */
export const resolveTimecoursePresetSelection = (
	preset: TimecoursePresetOption,
	trialAnalysisOptions: ProtocolDashboardOptions["trialAnalysisOptions"],
) => ({
	analysisId: preset.analysisId,
	analysisTypeId: preset.analysisTypeId,
	quantifiedRegionTypeId: preset.quantifiedRegionTypeId,
	isPlotUniformity: isUniformityAnalysis(trialAnalysisOptions, preset.analysisTypeId),
});

/** Narrowed to one analysis type and/or ortho when given. */
export const getQuantifiedRegionOptions = (
	results: AnalysisResultForOptions[],
	narrowTo?: Partial<Pick<TimecourseSelection, "analysisTypeId" | "orthoImageTypeId">>,
): DropdownOption[] => {
	return _.chain(results)
		.filter((r) => !!r.analysisTypeId && !!r.quantifiedRegionTypeId)
		.filter((r) => !narrowTo?.analysisTypeId || r.analysisTypeId === narrowTo.analysisTypeId)
		.filter((r) => !narrowTo?.orthoImageTypeId || r.orthoImageTypeId === narrowTo.orthoImageTypeId)
		.uniqBy("quantifiedRegionTypeId")
		.map((r) => ({
			key: r.quantifiedRegionTypeId!,
			value: r.quantifiedRegionTypeId!,
			text: r.quantifiedRegionTypeName ?? "",
		}))
		.value();
};

export const getDefaultQuantifiedRegionId = (results: AnalysisResultForOptions[]): string | undefined => {
	const options = getQuantifiedRegionOptions(results);

	return _.find(options, (o) => o.text.toLowerCase() === DEFAULT_QUANTIFIED_REGION_NAME)?.value ?? options[0]?.value;
};

/** One trial × analysis × analysis type × quantified region type the timecourse can show. */
export type TimecourseAnalysisOptionRow = AnalysisResultForOptions & {
	orthoImageTypeId: string;
	orthoImageTypeName: string;
};

/**
 * Flattens the dashboard's analysis options into one row per trial, the shape the timecourse
 * dropdown helpers take. This lets the dropdowns be built before any timecourse data is fetched.
 * Stand count analyses are left out, because the timecourse never shows them.
 */
export const getTimecourseAnalysisOptionRows = (
	trialAnalysisOptions: ProtocolDashboardOptions["trialAnalysisOptions"],
): TimecourseAnalysisOptionRow[] => {
	return _.chain(trialAnalysisOptions)
		.reject((o) => !!o.name?.includes("Stand Count"))
		.flatMap((o) =>
			_.map(o.trialIds, (trialId) => ({
				trialId,
				analysisId: o.id,
				analysisTypeId: o.analysisTypeId,
				analysisTypeName: o.analysisTypeName,
				analysisName: o.name,
				quantifiedRegionTypeId: o.quantifiedRegionTypeId,
				quantifiedRegionTypeName: o.quantifiedRegionTypeName,
				orthoImageTypeId: o.orthoImageTypeId,
				orthoImageTypeName: o.displayName,
			})),
		)
		.value();
};

/**
 * The time series presets the timecourse can offer. A preset needs an option row with its analysis
 * and analysis type, and its analysis must be in at least two trials, because an assessment carried
 * by a single trial cannot be mean-compared across trials.
 */
export const getComparableTimeSeriesPresets = (
	timeSeriesPresets: ProtocolTimeSeriesPreset[],
	optionRows: TimecourseAnalysisOptionRow[],
): ProtocolTimeSeriesPreset[] => {
	const trialsPerAnalysis = _.chain(optionRows)
		.groupBy("analysisId")
		.mapValues((rows) => _.uniqBy(rows, "trialId").length)
		.value();

	return _.filter(
		timeSeriesPresets,
		(tsp) =>
			(trialsPerAnalysis[tsp.analysisId] ?? 0) > 1 &&
			_.some(
				optionRows,
				(r) => r.analysisId === tsp.analysisId && r.analysisTypeId === tsp.analysisTypeId && !!r.orthoImageTypeId,
			),
	);
};

/* -------------------------------------------------------------------------- */
/*  Summary table configured assessments                                      */
/* -------------------------------------------------------------------------- */

/**
 * Builds the summary table's full assessment list: every curve model and stand count analysis,
 * then every ground data assessment/growth phase pairing.
 *
 * Curve models and stand counts default to "% MEAN" normalization; ground data defaults to "NONE",
 * since a ground rating is usually reported in its own units.
 */
export const buildConfiguredAssessments = (
	dashboardOptions: ProtocolDashboardOptions,
	assessmentOptions: AssessmentOption[],
	dataNormalizationOptions: DataNormalizationOption[],
): ProtocolAssessmentSelection[] => {
	const percentMean =
		_.find(dataNormalizationOptions, (o) => o.text.toUpperCase() === "% MEAN") ?? dataNormalizationOptions[0];
	const none = _.find(dataNormalizationOptions, (o) => o.text.toUpperCase() === "NONE") ?? dataNormalizationOptions[0];

	const analysisBacked = _.chain(assessmentOptions)
		.filter((ao) => ao.kind === "curvemodel" || ao.kind === "standcount")
		.map((ao): CurveModelAssessmentSelection | StandCountAssessmentSelection => {
			const base = {
				assessmentId: ao.value,
				assessmentName: ao.text,
				dataNormalizationId: percentMean?.value ?? null,
				dataNormalizationName: percentMean?.text ?? null,
				unitName: ao.unitName,
				checked: false,
				visible: true,
				analysisId: ao.analysisId!,
				analysisTypeId: ao.analysisTypeId!,
			};

			if (ao.kind === "standcount") {
				return {
					...base,
					kind: "standcount",
					curveModelAnalysisId: null,
					plannedTimingId: null,
					plannedTimingName: null,
				};
			}

			return {
				...base,
				kind: "curvemodel",
				// FIXME: index.tsx never sets curveModelId on the dropdown options, so it is always empty.
				// ProtocolSummaryTableEditor reads it in generateAssessmentType(), where it falls through
				// to curveModelAnalysisId.
				curveModelId: null,
				curveModelAnalysisId: ao.curveModelAnalysisId!,
				plannedTimingId: null,
				plannedTimingName: ao.plannedTimingName,
			};
		})
		.value();

	const groundData = _.map(
		dashboardOptions.groundDataAssessments,
		(gda): GroundDataAssessmentSelection => ({
			kind: "grounddata",
			assessmentId: gda.groundDataAssessmentId,
			assessmentName: gda.groundDataAssessmentName,
			plannedTimingId: gda.groundDataGrowthPhaseId,
			plannedTimingName: gda.groundDataGrowthPhaseName,
			dataNormalizationId: none?.value ?? null,
			dataNormalizationName: none?.text ?? null,
			unitName: gda.unitName,
			analysisId: null,
			analysisTypeId: null,
			curveModelAnalysisId: null,
			checked: false,
			visible: true,
		}),
	);

	return [...analysisBacked, ...groundData];
};

/**
 * Options for the site-specific table's assessment picker. One entry per configured assessment;
 * the key is synthetic because assessmentId alone is not unique (the same ground data assessment
 * appears once per growth phase).
 */
export type CombinedAssessmentOption = DropdownOption & {
	assessmentId: string;
	plannedTimingId: string | null;
	analysisTypeId: string | null;
};

export const buildCombinedAssessmentOptions = (
	selections: ProtocolAssessmentSelection[],
): CombinedAssessmentOption[] => {
	return _.map(selections, (s) => {
		const key = `${s.assessmentId}-${s.plannedTimingId ?? ""}-${s.analysisTypeId ?? ""}`;
		return {
			key,
			value: key,
			text: s.kind === "grounddata" ? `${s.assessmentName}, ${s.plannedTimingName}` : s.assessmentName,
			assessmentId: s.assessmentId,
			plannedTimingId: s.plannedTimingId,
			analysisTypeId: s.analysisTypeId,
		};
	});
};

/** The site-specific table defaults to Total Plant Health when the protocol has it. */
export const getDefaultCombinedAssessmentOption = (options: CombinedAssessmentOption[]) => {
	return _.find(options, (o) => o.text.includes("Total Plant Health")) ?? options[0];
};

/**
 * Applies a summary table preset by checking the assessments it names and unchecking the rest.
 *
 * Presets reference ground data and stand count analyses by a single id, but curve models by the
 * (curveModelAnalysisId, analysisId, analysisTypeId) triple — the same curve model fitted to a
 * different analysis is a different column.
 *
 * The "none" preset means the user is hand-picking, so selections are left untouched.
 */
export const applyPresetToConfiguredAssessments = (
	selections: ProtocolAssessmentSelection[],
	preset: ProtocolSummaryTablePreset | undefined,
): ProtocolAssessmentSelection[] => {
	if (!preset) return selections;

	const singleIds = new Set<string>();
	const curveModelRefs: {
		curveModelAnalysisId: string;
		analysisId: string | null;
		analysisTypeId: string | null;
	}[] = [];

	for (const section of preset.sections) {
		for (const pa of section.presetAnalyses) singleIds.add(pa.analysisId);
		for (const pgd of section.presetGroundData) singleIds.add(pgd.groundDataAssessmentId);
		curveModelRefs.push(...section.presetCurveModels);
	}

	return _.map(selections, (s) => ({
		...s,
		checked:
			singleIds.has(s.assessmentId) ||
			_.some(
				curveModelRefs,
				(ref) =>
					ref.curveModelAnalysisId === s.curveModelAnalysisId &&
					ref.analysisId === s.analysisId &&
					ref.analysisTypeId === s.analysisTypeId,
			),
	}));
};

/* -------------------------------------------------------------------------- */
/*  ProtocolSummaryTableEditor interop                                        */
/* -------------------------------------------------------------------------- */

/**
 * The assessment shape ProtocolSummaryTableEditor takes, with stringified booleans. It derives
 * `uniqueId` and `assessmentType` from these fields itself, so neither is sent.
 */
export type LegacyEditorAssessment = {
	assessmentId: string;
	assessmentName: string;
	plannedTimingId: string | null;
	plannedTimingName: string | null;
	dataNormalizationId: string | null;
	dataNormalizationName: string | null;
	isgrounddata: string;
	iscurvemodel: string;
	isStandCount: string;
	unitName: string | null;
	curveModelAnalysisId: string | null;
	curveModelId: string | null;
	analysisId: string | null;
	analysisTypeId: string | null;
	checked: boolean;
	visible: boolean;
};

export const toEditorAssessments = (selections: ProtocolAssessmentSelection[]): LegacyEditorAssessment[] => {
	return _.map(selections, (s) => ({
		assessmentId: s.assessmentId,
		assessmentName: s.assessmentName,
		plannedTimingId: s.plannedTimingId,
		plannedTimingName: s.plannedTimingName,
		dataNormalizationId: s.dataNormalizationId,
		dataNormalizationName: s.dataNormalizationName,
		isgrounddata: String(s.kind === "grounddata"),
		iscurvemodel: String(s.kind === "curvemodel"),
		isStandCount: String(s.kind === "standcount"),
		unitName: s.unitName,
		curveModelAnalysisId: s.curveModelAnalysisId,
		curveModelId: s.kind === "curvemodel" ? s.curveModelId : null,
		analysisId: s.analysisId,
		analysisTypeId: s.analysisTypeId,
		checked: s.checked,
		visible: s.visible,
	}));
};

/**
 * Maps the editor's Apply payload back onto the typed selections. Matched by identity rather than
 * rebuilt, so ordering changes made in the editor are preserved and no field is re-derived.
 */
export const fromEditorAssessments = (
	edits: LegacyEditorAssessment[],
	selections: ProtocolAssessmentSelection[],
): ProtocolAssessmentSelection[] => {
	const byIdentity = _.keyBy(
		selections,
		(s) => `${s.assessmentId}-${s.plannedTimingId ?? ""}-${s.analysisTypeId ?? ""}`,
	);

	return _.compact(
		_.map(edits, (e) => {
			const match = byIdentity[`${e.assessmentId}-${e.plannedTimingId ?? ""}-${e.analysisTypeId ?? ""}`];
			if (!match) return undefined;

			return {
				...match,
				checked: e.checked,
				visible: e.visible,
				dataNormalizationId: e.dataNormalizationId,
				dataNormalizationName: e.dataNormalizationName,
			};
		}),
	);
};

/* -------------------------------------------------------------------------- */
/*  Mean comparison payloads                                                  */
/* -------------------------------------------------------------------------- */

/** A plot analysis result row, narrowed to the value fields the mean comparison payload reads. */
type ValueBearingRow = {
	standCountAnalysisResultValue?: number | null;
	curveModelDataAnalysisId?: string | null;
	curveModelAnalysisValue?: number | null;
	assessmentId?: string | null;
	groundDatasetAssessmentValue?: number | null;
	plotAnalysisResultValue?: number | null;
};

/**
 * Picks the one value column that is populated for a row, in the order the summary table uses.
 *
 * Stand count wins when set, but a 0 counts as "not set" — the backend can send 0 for a missing
 * stand count. After that the row's own id fields say which column holds the value: a curve model
 * analysis, then a ground data assessment, then a plain plot analysis result.
 */
export const resolvePlotAnalysisValue = (row: ValueBearingRow): number | null => {
	if (row.standCountAnalysisResultValue !== null && row.standCountAnalysisResultValue !== 0) {
		return row.standCountAnalysisResultValue ?? null;
	}
	if (row.curveModelDataAnalysisId) return row.curveModelAnalysisValue ?? null;
	if (row.assessmentId) return row.groundDatasetAssessmentValue ?? null;

	return row.plotAnalysisResultValue ?? null;
};

/**
 * Builds the mean comparison request body for a protocol.
 *
 * The protocol pages post to the *timecourse* mean comparison endpoint with trial ids in the
 * flight/plot fields and sentinel -1 for dayOfYear and range. In the statistics API's
 * TimecourseData schema, plotId is a plain nullable string rather than a uuid, so a trial id is a
 * legal value.
 *
 * Rows with no value are dropped; the endpoint treats a missing value as a zero.
 */
export const buildMeanComparisonPayload = <TRow>(
	rows: TRow[],
	getValue: (row: TRow) => number | null | undefined,
	getKeys: (row: TRow) => { trialId: string; trialTreatmentId: number; plotReplicate: number },
): TimecourseDataViewModel => {
	const data = _.reduce(
		rows,
		(acc: TimecourseData[], row) => {
			const value = getValue(row);
			if (value === null || value === undefined) return acc;

			const { trialId, trialTreatmentId, plotReplicate } = getKeys(row);
			acc.push({
				flightId: trialId,
				plotId: trialId,
				dayOfYear: -1,
				trialTreatmentId,
				plotReplicate,
				range: -1,
				value,
				excludeFromAssessment: false,
				isOutlier3: false,
				isOutlier1_5: false,
			});
			return acc;
		},
		[],
	);

	return { data };
};

/* -------------------------------------------------------------------------- */
/*  Trial filters                                                             */
/* -------------------------------------------------------------------------- */

export const getTrialFilterOptions = (filters: ProtocolTrialFilter[] | undefined): TrialFilterOption[] => {
	return [
		..._.map(filters ?? [], (f) => ({
			key: f.id,
			value: f.id,
			text: f.name,
			filters: {
				quality: f.trialDataQualityIds,
				status: f.trialExclusionStatusIds,
				response: f.trialSiteResponseIds,
			},
		})),
		{
			key: CUSTOM_TRIAL_FILTER,
			value: CUSTOM_TRIAL_FILTER,
			text: "Custom",
			filters: { quality: [], status: [], response: [] },
		},
	];
};

/**
 * Trial ids a saved filter leaves checked.
 *
 * A filter lists the quality/status/response ids to *exclude*, so a trial survives only when it
 * matches none of them. Returns every trial id for the "Custom" pseudo-filter, which means the
 * user is picking trials by hand.
 */
export const getTrialIdsMatchingFilter = (
	filter: TrialFilterOption | undefined,
	trialInfos: FilterableTrialInfo[],
): string[] => {
	if (!filter || filter.value === CUSTOM_TRIAL_FILTER) return _.map(trialInfos, "trialId");

	return _.chain(trialInfos)
		.filter(
			(t) =>
				!filter.filters.status.includes(t.trialExclusionStatusId ?? "") &&
				!filter.filters.response.includes(t.trialSiteResponseId ?? "") &&
				!filter.filters.quality.includes(t.trialDataQualityId ?? ""),
		)
		.map("trialId")
		.value();
};

/** Both pages that initialize trial selection start from this filter rather than "all trials". */
export const getDefaultTrialFilter = (options: TrialFilterOption[]) => {
	return _.find(options, (o) => o.text === "Remove Excluded Trials");
};

/* -------------------------------------------------------------------------- */
/*  Misc                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Uniformity analyses are scored inversely, so the endpoints need to know. The protocol timecourse
 * decides this by name off the protocol's analysis options.
 */
export const isUniformityAnalysis = (
	trialAnalysisOptions: ProtocolDashboardOptions["trialAnalysisOptions"],
	analysisTypeId: string | null,
): boolean => {
	if (!analysisTypeId) return false;

	return _.find(trialAnalysisOptions, { analysisTypeId })?.analysisTypeName === "Uniformity";
};

/**
 * Stamps each row with whether its treatment and trial are in the given selection (a null
 * selection includes everything)
 */
export const applySelection = <TRow extends { trialTreatmentId: number; trialId: string }>(
	rows: TRow[] | null | undefined,
	selectedTreatmentMatrix: boolean[] | null,
	selectedTrialIds: string[] | null,
): (TRow & { treatmentChecked: boolean; trialChecked: boolean })[] | null => {
	if (!rows) return null;

	return _.map(rows, (row) => ({
		...row,
		treatmentChecked: selectedTreatmentMatrix ? !!selectedTreatmentMatrix[row.trialTreatmentId] : true,
		trialChecked: selectedTrialIds ? selectedTrialIds.includes(row.trialId) : true,
	}));
};

/**
 * Groups compiled mean comparison rows by assessment. An assessment whose treatments all got "a"
 * shows "-" instead, since no treatment separated from the others.
 */
export const buildMeanComparisonLetters = <TRow extends { assessmentId: string | null; tukeyLetters: string | null }>(
	rows: TRow[],
): Record<string, TRow[]> => {
	return _.mapValues(_.groupBy(rows, "assessmentId"), (assessmentRows) => {
		if (_.every(assessmentRows, (ar) => ar.tukeyLetters === "a")) {
			return _.map(assessmentRows, (ar) => ({ ...ar, tukeyLetters: "-" }));
		}

		return assessmentRows;
	});
};

/** Builds the boolean-per-treatment matrix the mean comparison endpoints expect. */
export const buildTreatmentMatrix = (treatments: ProtocolTreatment[], checkedTreatmentIds?: number[]): boolean[] => {
	const matrix: boolean[] = [];
	for (const t of _.orderBy(treatments, "trialTreatmentId")) {
		matrix[t.trialTreatmentId] = !checkedTreatmentIds || checkedTreatmentIds.includes(t.trialTreatmentId);
	}

	return matrix;
};

/** Treatment ids the matrix leaves unchecked — what the endpoints actually take. */
export const getUncheckedTreatmentIds = (matrix: boolean[] | null): number[] => {
	if (!matrix) return [];

	return _.chain(matrix)
		.map((checked, idx) => (checked ? undefined : idx))
		.compact()
		.value();
};

/** Trial ids not selected, relative to the protocol's full trial list. */
export const getUncheckedTrialIds = (allTrialIds: string[], selectedTrialIds: string[] | null): string[] => {
	if (!selectedTrialIds) return [];

	return _.difference(allTrialIds, selectedTrialIds);
};
